import { getDisplayToken } from '@/domain/hooks/useDisplayToken'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import { SuccessViewCheckmark } from '@/features/dialogs/common/components/success-view/SuccessViewCheckmark'
import { SuccessViewContent } from '@/features/dialogs/common/components/success-view/SuccessViewContent'
import { SuccessViewProceedButton } from '@/features/dialogs/common/components/success-view/SuccessViewProceedButton'
import { SuccessViewSummaryPanel } from '@/features/dialogs/common/components/success-view/SuccessViewSummaryPanel'
import { TokenIcon } from '@/ui/atoms/token-icon/TokenIcon'
import { Typography } from '@/ui/atoms/typography/Typography'
import { useChainId } from 'wagmi'

export interface MultiplySuccessViewProps {
  collateralToken: Token
  collateralAmount: NormalizedUnitNumber
  borrowToken: Token
  borrowAmount: NormalizedUnitNumber
  multiplier?: number
  onProceed: () => void
  proceedText?: string
}

export function MultiplySuccessView({
  collateralToken,
  collateralAmount,
  borrowToken,
  borrowAmount,
  multiplier,
  onProceed,
  proceedText = 'Done',
}: MultiplySuccessViewProps) {
  const chainId = useChainId()
  const displayCollateralToken = getDisplayToken(chainId, collateralToken)
  const displayBorrowToken = getDisplayToken(chainId, borrowToken)

  const hasCollateral = collateralAmount && Number(collateralAmount) > 0
  const hasBorrow = borrowAmount && Number(borrowAmount) > 0

  return (
    <SuccessViewContent>
      <SuccessViewCheckmark isModal />

      <div className="flex w-full flex-col gap-3">
        {/* Deposited Collateral Panel */}
        {hasCollateral && (
          <SuccessViewSummaryPanel title="Deposited">
            <div className="flex items-center gap-2">
              <TokenIcon token={collateralToken} className="h-6" />
              <span className="font-semibold">{displayCollateralToken.symbol}</span>
            </div>
            <div className="flex grow flex-col">
              <Typography className="text-right font-primary">
                {collateralToken.format(collateralAmount, { style: 'auto' })}
              </Typography>
            </div>
          </SuccessViewSummaryPanel>
        )}

        {/* Borrowed Panel */}
        {hasBorrow && (
          <SuccessViewSummaryPanel title="Borrowed">
            <div className="flex items-center gap-2">
              <TokenIcon token={borrowToken} className="h-6" />
              <span className="font-semibold">{displayBorrowToken.symbol}</span>
            </div>
            <div className="flex grow flex-col">
              <Typography className="text-right font-primary">
                {borrowToken.format(borrowAmount, { style: 'auto' })}
              </Typography>
            </div>
          </SuccessViewSummaryPanel>
        )}

        {/* Multiplier - inline display */}
        {multiplier && (
          <SuccessViewSummaryPanel>
            <span className="font-semibold">Multiplier</span>
            <Typography className="font-primary text-primary">{multiplier.toFixed(2)}x</Typography>
          </SuccessViewSummaryPanel>
        )}
      </div>

      <SuccessViewProceedButton onProceed={onProceed}>{proceedText}</SuccessViewProceedButton>
    </SuccessViewContent>
  )
}
